import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useLocation } from 'react-router';
import { useMe } from '@posilka/client/api/me';
import { useAppStore } from '@/lib/store';
import { BackButtonSync } from './BackButtonSync';
import { BrandMark } from './BrandMark';
import { DeepLinkHandler } from './DeepLinkHandler';
import { Icon, type IconName } from './Icon';

interface Tab {
  to: string;
  key: string;
  icon: IconName;
}

const SENDER_TABS: Tab[] = [
  { to: '/', key: 'nav.home', icon: 'home' },
  { to: '/orders', key: 'nav.orders', icon: 'box' },
  { to: '/profile', key: 'nav.profile', icon: 'user' },
];

const DRIVER_TABS: Tab[] = [
  { to: '/', key: 'nav.available', icon: 'search' },
  { to: '/driver/active', key: 'nav.myDeliveries', icon: 'truck' },
  { to: '/driver/routes', key: 'nav.routes', icon: 'route' },
  { to: '/profile', key: 'nav.profile', icon: 'user' },
];

export function Layout() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const mode = useAppStore((s) => s.mode);
  const { data: me } = useMe();

  let tabs = mode === 'driver' ? DRIVER_TABS : SENDER_TABS;
  if (me?.isAdmin)
    tabs = [...tabs.slice(0, -1), { to: '/admin', key: 'nav.admin', icon: 'shield' }, tabs.at(-1)!];
  // Pastki navigatsiya faqat tab sahifalarida (forma/tafsilotda MainButton bilan to'qnashmasin).
  const showNav = tabs.some((tab) => tab.to === pathname);

  return (
    <div className="flex min-h-dvh flex-col">
      <BackButtonSync />
      <DeepLinkHandler />
      {showNav && (
        <header className="flex items-center gap-2.5 px-5 pt-4">
          <BrandMark className="h-8 w-8 rounded-[9px] shadow-[0_4px_18px_-6px_var(--glow)]" />
          <span className="font-display text-[15px] font-bold tracking-tight">
            Jo&apos;natmaBor
          </span>
          {mode === 'driver' && (
            <span className="ml-auto rounded-full border border-line px-2.5 py-1 text-xs font-semibold text-muted">
              🚕 {t('mode.driver')}
            </span>
          )}
        </header>
      )}
      <main className={`flex-1 px-4 pt-4 ${showNav ? 'pb-28' : 'pb-6'}`}>
        <Outlet />
      </main>
      {showNav && (
        <nav className="glass fixed inset-x-3 bottom-[max(12px,env(safe-area-inset-bottom))] z-10 flex gap-1 rounded-[22px] p-1.5 shadow-[0_12px_40px_-12px_rgba(0,0,0,0.5)]">
          {tabs.map((tab) => (
            <NavLink
              key={tab.to}
              to={tab.to}
              end
              className={({ isActive }) =>
                `press flex min-w-0 flex-1 flex-col items-center gap-1 rounded-2xl py-2 text-[11px] font-semibold transition-colors ${
                  isActive ? 'bg-lime text-lime-ink' : 'text-muted'
                }`
              }
            >
              <Icon name={tab.icon} className="h-[22px] w-[22px]" />
              <span className="max-w-full truncate px-1">{t(tab.key)}</span>
            </NavLink>
          ))}
        </nav>
      )}
    </div>
  );
}
